// web/src/lib/roasts.ts
// Task 30: Firestore artifact persistence helpers.
// Exports: saveRoast, loadRoast, listMyRoasts, incrementViewCount.
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  increment,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";
import type { RoastArtifact, Mode, Profanity, WordTiming, DetectedObject, CensorRegion, AspectRatio, SlideScheduleEntry, DeckVerdict, SourceType } from "@/types/roast";
import type { AudioAssets } from "@/player/audio/types";
import { DEFAULT_AUDIO_PROFILE } from "@/player/audio/types";
import type { CaptionMask, BleepMode } from "@/config/censorship";
import { DEFAULT_CAPTION_MASK, DEFAULT_BLEEP_MODE, LEGACY_CAPTION_MASK, LEGACY_BLEEP_MODE } from "@/config/censorship";

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/** Convert a base64 string to a Blob. */
function base64ToBlob(b64: string, mimeType: string): Blob {
  const bytes = atob(b64);
  const buf = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
  return new Blob([buf], { type: mimeType });
}

// ---------------------------------------------------------------------------
// saveRoast params
// ---------------------------------------------------------------------------

export interface SaveRoastParams {
  /** Explicit roast id. Generated via crypto.randomUUID() if omitted. */
  id?: string;
  ownerUid: string;
  ownerEmail: string;
  mode: Mode;
  profanity: Profanity;
  title: string;
  script: string;
  scriptOriginal: string;
  name: string | null;
  context: string | null;
  wordTimings: WordTiming[];        // seconds
  detectedObjects: DetectedObject[];
  censorRegions: CensorRegion[];
  voiceName: string;
  musicTrack: string | null;
  durationSec: number;              // seconds
  isCharacterFullscreen: boolean;
  aspect: AspectRatio;
  /** Opt-in censorship flags (omit ⇒ persisted as today's defaults). */
  captionMask?: CaptionMask;
  bleepMode?: BleepMode;
  /** Source image as a File or base64 string. Photo source only. */
  imageFile?: File | string;
  /** MIME type — required when imageFile is a base64 string. */
  imageMimeType?: string;
  /** Raw audio bytes as base64 (output of synthesizeSpeech). */
  audioBase64: string;
  /** Raw doodle PNG bytes as base64 (output of generateDoodle). Omit when no doodle. */
  doodleBase64?: string;
  /** Resolved audio assets (music track + SFX cues + source profile). */
  audio: AudioAssets;
  // ---- deck source ----
  sourceType?: SourceType;                       // default "photo"
  deckFile?: Blob;                               // PDF → roasts/{id}/deck.pdf
  deckKeySlides?: { slideIndex: number; blob: Blob; detectedObjects: DetectedObject[]; doodleBlob?: Blob }[];
  deckFillerImages?: { slideIndex: number; blob: Blob }[];
  slideSchedule?: SlideScheduleEntry[];
  verdict?: DeckVerdict | null;
}

// ---------------------------------------------------------------------------
// Firestore CRUD helpers
// ---------------------------------------------------------------------------

/**
 * Upload ALL replay assets to the corp-readable `roasts/{roastId}/` path and
 * write the Firestore `roasts/{roastId}` document.
 *
 * Assets uploaded:
 *   roasts/{roastId}/image.jpg   — source image (File or base64)
 *   roasts/{roastId}/audio.wav   — synthesized speech (base64 → Blob)
 *   roasts/{roastId}/doodle.png  — AI doodle (base64 → Blob, only when doodleBase64 is set)
 *
 * Storage rules: `allow read: if corp()` on `roasts/{roastId}/{file}` so any
 * authenticated corp user can replay via /r/:id.
 * Firestore rules enforce ownerUid create-immutability — that is the real
 * ownership control; corp-write to the storage replay path is acceptable for an
 * internal tool.
 *
 * Times: wordTimings in SECONDS; createdAt / updatedAt in epoch-ms.
 *
 * @returns The roast id (passed-in or newly generated).
 */
export async function saveRoast(params: SaveRoastParams): Promise<string> {
  const roastId =
    params.id ??
    (typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const now = Date.now();

  if (params.sourceType === "deck" || params.sourceType === "doc") return saveDeckRoast(roastId, now, params);

  if (params.imageFile == null) {
    throw new Error("saveRoast: imageFile is required for a photo roast.");
  }

  // The three uploads target distinct Storage paths (image.jpg / audio.wav /
  // doodle.png), so they are independent and can run concurrently. The Firestore
  // write below still waits for all of them via Promise.all.

  // --- Image upload ---
  // imageFile is required for photo path (deck is dispatched above).
  const imageFileSrc = params.imageFile;
  const imageBlob =
    imageFileSrc instanceof File
      ? imageFileSrc
      : base64ToBlob(imageFileSrc, params.imageMimeType ?? "image/jpeg");
  const imageMime =
    imageFileSrc instanceof File
      ? imageFileSrc.type || "image/jpeg"
      : (params.imageMimeType ?? "image/jpeg");
  const imagePath = `roasts/${roastId}/image.jpg`;
  const imageRef = ref(storage, imagePath);
  const imageUpload = uploadBytes(imageRef, imageBlob, {
    contentType: imageMime,
  }).then(() => getDownloadURL(imageRef));

  // --- Audio upload ---
  const audioBlob = base64ToBlob(params.audioBase64, "audio/wav");
  const audioPath = `roasts/${roastId}/audio.wav`;
  const audioRef = ref(storage, audioPath);
  const audioUpload = uploadBytes(audioRef, audioBlob, {
    contentType: "audio/wav",
  }).then(() => getDownloadURL(audioRef));

  // --- Doodle upload (optional) ---
  let doodleImagePath: string | undefined;
  let doodleUpload: Promise<string | undefined> = Promise.resolve(undefined);
  if (params.doodleBase64) {
    const doodleBlob = base64ToBlob(params.doodleBase64, "image/png");
    doodleImagePath = `roasts/${roastId}/doodle.png`;
    const doodleRef = ref(storage, doodleImagePath);
    doodleUpload = uploadBytes(doodleRef, doodleBlob, {
      contentType: "image/png",
    }).then(() => getDownloadURL(doodleRef));
  }

  // Run the uploads concurrently; the Firestore write waits for all of them.
  const [imageUrl, audioUrl, doodleImageUrl] = await Promise.all([
    imageUpload,
    audioUpload,
    doodleUpload,
  ]);

  // --- Assemble Firestore doc ---
  const artifact: RoastArtifact = {
    id: roastId,
    ownerUid: params.ownerUid,
    ownerEmail: params.ownerEmail,
    createdAt: now,
    updatedAt: now,
    status: "ready",
    visibility: "unlisted",
    mode: params.mode,
    profanity: params.profanity,
    title: params.title,
    script: params.script,
    scriptOriginal: params.scriptOriginal,
    name: params.name,
    context: params.context,
    imagePath,
    imageUrl,
    audioPath,
    audioUrl,
    musicTrack: params.musicTrack,
    durationSec: params.durationSec,
    wordTimings: params.wordTimings,
    detectedObjects: params.detectedObjects,
    censorRegions: params.censorRegions,
    voiceName: params.voiceName,
    isCharacterFullscreen: params.isCharacterFullscreen,
    aspect: params.aspect,
    viewCount: 0,
    audio: params.audio,
    captionMask: params.captionMask ?? DEFAULT_CAPTION_MASK,
    bleepMode: params.bleepMode ?? DEFAULT_BLEEP_MODE,
    ...(doodleImagePath != null ? { doodleImagePath, doodleImageUrl } : {}),
  };

  await setDoc(doc(db, "roasts", roastId), artifact);
  return roastId;
}

/** Deck save: re-upload the client-held rasters + the PDF + audio to
 *  roasts/{id}/, then write the deck Firestore doc. */
async function saveDeckRoast(
  roastId: string, now: number, params: SaveRoastParams,
): Promise<string> {
  const keySrc = params.deckKeySlides ?? [];
  const fillerSrc = params.deckFillerImages ?? [];

  const keyUploads = keySrc.map(async (k) => {
    const path = `roasts/${roastId}/key-${k.slideIndex}.jpg`;
    const r = ref(storage, path);
    await uploadBytes(r, k.blob, { contentType: k.blob.type || "image/jpeg" });
    const imageUrl = await getDownloadURL(r);
    let doodleImageUrl: string | undefined; let doodlePath: string | undefined;
    if (k.doodleBlob) {
      doodlePath = `roasts/${roastId}/key-${k.slideIndex}-doodle.jpg`;
      const dr = ref(storage, doodlePath);
      await uploadBytes(dr, k.doodleBlob, { contentType: k.doodleBlob.type || "image/png" });
      doodleImageUrl = await getDownloadURL(dr);
    }
    return { slideIndex: k.slideIndex, imageUrl, imagePath: path, detectedObjects: k.detectedObjects, ...(doodleImageUrl ? { doodleImageUrl, doodlePath } : {}) };
  });
  const fillerUploads = fillerSrc.map(async (f) => {
    const path = `roasts/${roastId}/filler-${f.slideIndex}.jpg`;
    const r = ref(storage, path);
    await uploadBytes(r, f.blob, { contentType: f.blob.type || "image/jpeg" });
    const imageUrl = await getDownloadURL(r);
    return { slideIndex: f.slideIndex, imageUrl, imagePath: path };
  });

  const audioPath = `roasts/${roastId}/audio.wav`;
  const audioRef = ref(storage, audioPath);
  const audioUpload = uploadBytes(audioRef, base64ToBlob(params.audioBase64, "audio/wav"), {
    contentType: "audio/wav",
  }).then(() => getDownloadURL(audioRef));

  // Fix C1: the source PDF is never read back anywhere in the app, so uploading
  // it is both wasteful and a correctness hazard: the 25MB storage cap can reject
  // a valid 25–50MB deck after it has already been comprehended + voiced, orphaning
  // the already-uploaded rasters. We simply skip the upload and set deckPdfPath: null.

  const [keySlides, fillerImages, audioUrl] = await Promise.all([
    Promise.all(keyUploads),
    Promise.all(fillerUploads),
    audioUpload,
  ]);

  const artifact: RoastArtifact = {
    id: roastId, ownerUid: params.ownerUid, ownerEmail: params.ownerEmail,
    createdAt: now, updatedAt: now, status: "ready", visibility: "unlisted",
    mode: params.mode, profanity: params.profanity, title: params.title,
    script: params.script, scriptOriginal: params.scriptOriginal, name: params.name, context: params.context,
    imagePath: null, imageUrl: null, audioPath, audioUrl,
    musicTrack: params.musicTrack, durationSec: params.durationSec,
    wordTimings: params.wordTimings, detectedObjects: [], censorRegions: params.censorRegions,
    voiceName: params.voiceName, isCharacterFullscreen: false, aspect: params.aspect, viewCount: 0,
    audio: params.audio,
    captionMask: params.captionMask ?? DEFAULT_CAPTION_MASK,
    bleepMode: params.bleepMode ?? DEFAULT_BLEEP_MODE,
    sourceType: params.sourceType, deckPdfPath: null,
    keySlides, fillerImages, slideSchedule: params.slideSchedule ?? [],
    verdict: params.verdict ?? null,
  };
  await setDoc(doc(db, "roasts", roastId), artifact);
  return roastId;
}

/**
 * Fetch a single roast by Firestore document id.
 * Returns null if the document does not exist.
 */
export async function loadRoast(id: string): Promise<RoastArtifact | null> {
  const snap = await getDoc(doc(db, "roasts", id));
  if (!snap.exists()) return null;
  const data = snap.data() as RoastArtifact;
  return {
    ...data,
    aspect: data.aspect ?? "9:16",
    audio: data.audio ?? { musicTrack: "RoastMusic_Jazzy", cues: [], profile: DEFAULT_AUDIO_PROFILE },
    // Legacy roasts predate these fields → keep today's masked/bleeped display, NOT the new
    // clean-launch default (which would retroactively uncensor their real-curse audio).
    captionMask: data.captionMask ?? LEGACY_CAPTION_MASK,
    bleepMode: data.bleepMode ?? LEGACY_BLEEP_MODE,
    sourceType: data.sourceType ?? "photo",
    deckPdfPath: data.deckPdfPath ?? null,
    keySlides: data.keySlides ?? undefined,
    fillerImages: data.fillerImages ?? undefined,
    slideSchedule: data.slideSchedule ?? undefined,
    verdict: data.verdict ?? null,
  };
}

/**
 * List all roasts owned by uid, newest-first.
 * Matches the composite index: ownerUid ASC, createdAt DESC.
 * Firestore rules allow list only when ownerUid == request.auth.uid.
 */
export async function listMyRoasts(uid: string): Promise<RoastArtifact[]> {
  const q = query(
    collection(db, "roasts"),
    where("ownerUid", "==", uid),
    orderBy("createdAt", "desc"),
  );
  const snap = await getDocs(q);
  // Mirror loadRoast: default a missing aspect and audio on legacy docs.
  return snap.docs.map((d) => {
    const data = d.data() as RoastArtifact;
    return {
      ...data,
      aspect: data.aspect ?? "9:16",
      audio: data.audio ?? { musicTrack: "RoastMusic_Jazzy", cues: [], profile: DEFAULT_AUDIO_PROFILE },
      // Legacy roasts predate these fields → keep today's masked/bleeped display (see loadRoast).
      captionMask: data.captionMask ?? LEGACY_CAPTION_MASK,
      bleepMode: data.bleepMode ?? LEGACY_BLEEP_MODE,
      sourceType: data.sourceType ?? "photo",
      deckPdfPath: data.deckPdfPath ?? null,
      keySlides: data.keySlides ?? undefined,
      fillerImages: data.fillerImages ?? undefined,
      slideSchedule: data.slideSchedule ?? undefined,
      verdict: data.verdict ?? null,
    };
  });
}

/**
 * Atomically increment a roast's viewCount by 1.
 * Used by the /r/:id replay route (Task 32).
 */
export async function incrementViewCount(id: string): Promise<void> {
  await updateDoc(doc(db, "roasts", id), { viewCount: increment(1) });
}

/**
 * Delete a roast: remove every Storage object it references (best-effort — a
 * missing object is ignored so it never blocks the rest), then the Firestore doc.
 * Works for both photo (image/audio/doodle) and deck (key/filler rasters + pdf + audio).
 */
export async function deleteRoast(artifact: RoastArtifact): Promise<void> {
  const paths: string[] = [
    artifact.imagePath ?? undefined,
    artifact.audioPath ?? undefined,
    artifact.doodleImagePath ?? undefined,
    artifact.deckPdfPath ?? undefined,
    ...(artifact.keySlides?.map((k) => k.imagePath) ?? []),
    ...(artifact.keySlides?.map((k) => k.doodlePath) ?? []),
    ...(artifact.fillerImages?.map((f) => f.imagePath) ?? []),
  ].filter((p): p is string => !!p);

  await Promise.all(
    paths.map((p) =>
      deleteObject(ref(storage, p)).catch((e) => {
        console.error(`deleteRoast: failed to delete ${p}`, e);
      }),
    ),
  );
  await deleteDoc(doc(db, "roasts", artifact.id));
}
